import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  ScholarshipState,
  ScholarshipTestSession,
  ScholarshipAnswerValue,
  ScholarshipProfileFormData,
  ScholarshipWarnings,
  ScholarshipSubmitResult,
} from "@/store/types/ScholarshipTestType";
import {
  getScholarshipDashboardResult,
  getScholarshipDashboardTest,
} from "./scholarshipApi";

const EMPTY_PROFILE: ScholarshipProfileFormData = {
  interestedCountry: "",
  qualification: "",
  degreeLevelId: "",
  completionYear: "",
  score: "",
  englishProficiencyTest: "",
  englishProficiencyScore: "",
  boardName: "",
  englishMarks: "",
  stream: "",
  specializationId: "",
};

const EMPTY_WARNINGS: ScholarshipWarnings = {
  tabSwitches: 0,
  fullscreenExits: 0,
  total: 0,
  lastReason: null,
  lastOccurredAt: null,
};

const initialState: ScholarshipState = {
  dashboardTest: null,
  testResult: null,
  loading: false,
  resultLoading: false,
  error: null,
  activeSession: null,
  answers: {},
  profile: EMPTY_PROFILE,
  currentSectionIndex: 0,
  remainingSeconds: 15 * 60,
  warnings: EMPTY_WARNINGS,
  submitResult: null,
};

const scholarshipSlice = createSlice({
  name: "scholarship",
  initialState,
  reducers: {
    clearScholarship: () => initialState,
    clearScholarshipResult: (state) => {
      state.testResult = null;
    },
    setActiveSession: (
      state,
      action: PayloadAction<ScholarshipTestSession | null>
    ) => {
      state.activeSession = action.payload;
    },
    setAnswer: (
      state,
      action: PayloadAction<{ questionId: string; value: ScholarshipAnswerValue }>
    ) => {
      state.answers[action.payload.questionId] = action.payload.value;
    },
    setAllAnswers: (
      state,
      action: PayloadAction<Record<string, ScholarshipAnswerValue>>
    ) => {
      state.answers = action.payload;
    },
    setProfileData: (
      state,
      action: PayloadAction<ScholarshipProfileFormData>
    ) => {
      state.profile = action.payload;
    },
    updateProfileField: (
      state,
      action: PayloadAction<{
        field: keyof ScholarshipProfileFormData;
        value: string;
      }>
    ) => {
      state.profile[action.payload.field] = action.payload.value;
    },
    setCurrentSectionIndex: (state, action: PayloadAction<number>) => {
      state.currentSectionIndex = action.payload;
    },
    setRemainingSeconds: (state, action: PayloadAction<number>) => {
      state.remainingSeconds = action.payload;
    },
    setWarnings: (state, action: PayloadAction<ScholarshipWarnings>) => {
      state.warnings = action.payload;
    },
    setSubmitResult: (
      state,
      action: PayloadAction<ScholarshipSubmitResult | null>
    ) => {
      state.submitResult = action.payload;
    },
    resetActiveTestState: (state) => {
      state.activeSession = null;
      state.answers = {};
      state.profile = EMPTY_PROFILE;
      state.currentSectionIndex = 0;
      state.remainingSeconds = 15 * 60;
      state.warnings = EMPTY_WARNINGS;
      state.submitResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getScholarshipDashboardTest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getScholarshipDashboardTest.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardTest = action.payload;
        if (!action.payload?.testId) {
          state.testResult = null;
        }
      })
      .addCase(getScholarshipDashboardTest.rejected, (state, action) => {
        state.loading = false;
        state.dashboardTest = null;
        state.testResult = null;
        state.error =
          action.error.message ?? "Unable to load scholarship test status.";
      })
      .addCase(getScholarshipDashboardResult.pending, (state) => {
        state.resultLoading = true;
        state.error = null;
      })
      .addCase(getScholarshipDashboardResult.fulfilled, (state, action) => {
        state.resultLoading = false;
        state.testResult = action.payload;
      })
      .addCase(getScholarshipDashboardResult.rejected, (state, action) => {
        state.resultLoading = false;
        state.testResult = null;
        state.error =
          action.error.message ?? "Unable to load scholarship test result.";
      });
  },
});

export const {
  clearScholarship,
  clearScholarshipResult,
  setActiveSession,
  setAnswer,
  setAllAnswers,
  setProfileData,
  updateProfileField,
  setCurrentSectionIndex,
  setRemainingSeconds,
  setWarnings,
  setSubmitResult,
  resetActiveTestState,
} = scholarshipSlice.actions;

export default scholarshipSlice.reducer;
